
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface AccessCode {
  id: string;
  booking_id: string;
  code: string;
  start_time: string;
  is_active: boolean;
  created_at: string;
  time_slot_id?: string;
  seam_access_code_id?: string;
}

export interface DeviceInfo {
  id: string;
  device_name: string;
  is_enabled: boolean;
  seam_device_id?: string;
}

export const useAccessCodes = (bookingId?: string) => {
  return useQuery({
    queryKey: ['access-codes', bookingId],
    queryFn: async () => {
      if (!bookingId) {
        console.log('useAccessCodes: No booking ID provided');
        return { accessCodes: [], devices: [], timeSlotCodes: [] };
      }

      console.log('useAccessCodes: Fetching access codes for booking:', bookingId);
      
      // First, get the booking details to find the time_slot_id
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select('provider_id, time_slot_id')
        .eq('id', bookingId)
        .single();

      if (bookingError) {
        console.error('useAccessCodes: Error fetching booking:', bookingError);
        throw bookingError;
      }

      console.log('useAccessCodes: Booking details:', { 
        bookingId, 
        providerId: booking.provider_id, 
        timeSlotId: booking.time_slot_id 
      });

      // Fetch access codes directly linked to this booking
      const { data: bookingAccessCodes, error: bookingCodesError } = await supabase
        .from('access_codes')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false });

      if (bookingCodesError) {
        console.error('useAccessCodes: Error fetching booking access codes:', bookingCodesError);
        throw bookingCodesError;
      }

      console.log('useAccessCodes: Found booking-specific access codes:', bookingAccessCodes);

      // Fetch access codes for the same time slot (potential reusable codes)
      let timeSlotAccessCodes: AccessCode[] = [];
      if (booking.time_slot_id) {
        console.log('useAccessCodes: Fetching time slot codes for time_slot_id:', booking.time_slot_id);
        
        const { data: timeSlotCodes, error: timeSlotCodesError } = await supabase
          .from('access_codes')
          .select('*')
          .eq('time_slot_id', booking.time_slot_id)
          .neq('booking_id', bookingId) // Exclude codes already linked to this booking
          .order('created_at', { ascending: false });

        if (timeSlotCodesError) {
          console.error('useAccessCodes: Error fetching time slot access codes:', timeSlotCodesError);
          // Don't throw here - just log and continue with empty array
        } else {
          timeSlotAccessCodes = timeSlotCodes as AccessCode[] || [];
          console.log('useAccessCodes: Found time slot access codes:', timeSlotAccessCodes);
        }
      } else {
        console.log('useAccessCodes: No time_slot_id found, skipping time slot code fetch');
      }

      console.log('TIMEZONE DEBUG - FETCHED ACCESS CODES:', {
        bookingAccessCodes,
        timeSlotAccessCodes,
        TIMEZONE_VALIDATION: bookingAccessCodes?.map(code => ({
          id: code.id,
          code: code.code,
          start_time_raw: code.start_time,
          start_time_parsed_utc: new Date(code.start_time).toISOString(),
          start_time_parsed_local: new Date(code.start_time).toLocaleString('en-GB', { timeZone: 'Europe/London' }),
          created_at: code.created_at,
          time_slot_id: code.time_slot_id,
          seam_access_code_id: code.seam_access_code_id,
          note: 'Checking if stored start_time matches expected format'
        }))
      });

      // Fetch devices for this provider to check if any are enabled
      const { data: devices, error: devicesError } = await supabase
        .from('devices')
        .select('id, device_name, is_enabled, seam_device_id')
        .eq('provider_id', booking.provider_id)
        .eq('is_active', true);

      if (devicesError) {
        console.error('useAccessCodes: Error fetching devices:', devicesError);
        throw devicesError;
      }

      console.log('useAccessCodes: Fetched devices:', devices);

      return {
        accessCodes: bookingAccessCodes as AccessCode[],
        timeSlotCodes: timeSlotAccessCodes as AccessCode[],
        devices: devices as DeviceInfo[]
      };
    },
    enabled: !!bookingId,
  });
};

export const useCreateAccessCode = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      bookingId,
      seamDeviceId,
      startTime,
      endTime,
      timeSlotId,
    }: {
      bookingId: string;
      seamDeviceId: string;
      startTime: string;
      endTime: string;
      timeSlotId?: string;
    }) => {
      console.log('TIMEZONE DEBUG - MUTATION INPUT:', {
        bookingId,
        seamDeviceId,
        startTime,
        endTime,
        timeSlotId,
        TIMEZONE_ANALYSIS: {
          startTime_parsed_utc: new Date(startTime).toISOString(),
          startTime_parsed_local: new Date(startTime).toLocaleString('en-GB', { timeZone: 'Europe/London' }),
          endTime_parsed_utc: new Date(endTime).toISOString(),
          endTime_parsed_local: new Date(endTime).toLocaleString('en-GB', { timeZone: 'Europe/London' }),
          current_utc: new Date().toISOString(),
          current_local: new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' }),
          note: 'Input times that will be sent to Seam API'
        }
      });

      // FIXED LOGIC: Check for existing access codes for this time slot
      if (timeSlotId) {
        console.log('useCreateAccessCode: Checking for existing access code for time slot:', timeSlotId);
        
        // Query for ANY existing access code with this time_slot_id (not just active ones)
        const { data: existingTimeSlotCodes, error: timeSlotCodeError } = await supabase
          .from('access_codes')
          .select('*')
          .eq('time_slot_id', timeSlotId);

        if (timeSlotCodeError) {
          console.error('useCreateAccessCode: Error checking for existing time slot codes:', timeSlotCodeError);
          throw new Error(`Failed to check for existing time slot codes: ${timeSlotCodeError.message}`);
        }

        console.log('useCreateAccessCode: Found existing codes for time slot:', existingTimeSlotCodes);

        // Check if we found any existing codes for this time slot
        if (existingTimeSlotCodes && existingTimeSlotCodes.length > 0) {
          const existingAccessCode = existingTimeSlotCodes[0];
          console.log('useCreateAccessCode: Found existing access code for time slot:', existingAccessCode);
          
          // Check if this booking already has an access code record
          const existingBookingCode = existingTimeSlotCodes.find(code => code.booking_id === bookingId);
          
          if (existingBookingCode) {
            console.log('useCreateAccessCode: Booking already has access code record:', existingBookingCode);
            
            // Update the booking with check-in timestamp since code already exists for this booking
            const { error: updateError } = await supabase
              .from('bookings')
              .update({
                checked_in_at: new Date().toISOString()
              })
              .eq('id', bookingId);

            if (updateError) {
              console.error('useCreateAccessCode: Failed to update booking check-in time:', updateError);
            } else {
              console.log('useCreateAccessCode: Updated booking with check-in timestamp');
            }

            return {
              seamResponse: { 
                access_code: { access_code_id: existingBookingCode.seam_access_code_id },
                fallback_used: false,
                existing_code_reused: true
              },
              accessCode: existingBookingCode
            };
          } else {
            // Create a new access_codes record for this booking that references the same code
            const { data: newAccessCodeRecord, error: insertError } = await supabase
              .from('access_codes')
              .insert({
                booking_id: bookingId,
                code: existingAccessCode.code,
                start_time: existingAccessCode.start_time,
                time_slot_id: timeSlotId,
                seam_access_code_id: existingAccessCode.seam_access_code_id,
                is_active: true,
              })
              .select()
              .single();

            if (insertError) {
              console.error('useCreateAccessCode: Failed to create access code record for this booking:', insertError);
              throw new Error(`Failed to create access code record: ${insertError.message}`);
            }

            // Update the booking with check-in timestamp since we're reusing an existing code
            const { error: updateError } = await supabase
              .from('bookings')
              .update({
                checked_in_at: new Date().toISOString()
              })
              .eq('id', bookingId);

            if (updateError) {
              console.error('useCreateAccessCode: Failed to update booking check-in time:', updateError);
            } else {
              console.log('useCreateAccessCode: Updated booking with check-in timestamp');
            }

            console.log('TIMEZONE DEBUG - EXISTING CODE REUSED:', {
              existing_code: existingAccessCode.code,
              existing_start_time: existingAccessCode.start_time,
              existing_time_slot_id: existingAccessCode.time_slot_id,
              existing_seam_access_code_id: existingAccessCode.seam_access_code_id,
              new_record_for_booking: newAccessCodeRecord,
              note: 'Reused existing access code and created record for this booking'
            });

            return {
              seamResponse: { 
                access_code: { access_code_id: existingAccessCode.seam_access_code_id },
                fallback_used: false,
                existing_code_reused: true
              },
              accessCode: newAccessCodeRecord
            };
          }
        }

        console.log('useCreateAccessCode: No existing access code found for time slot, proceeding to create new one');
      }

      // Generate a random 4-digit access code
      const accessCode = Math.floor(1000 + Math.random() * 9000).toString();

      // Call the Supabase edge function
      const { data, error } = await supabase.functions.invoke('seam-integration', {
        body: {
          action: 'create_access_code',
          deviceId: seamDeviceId,
          accessCode: accessCode,
          startTime: startTime,
          endTime: endTime,
        },
      });

      if (error) {
        console.error('useCreateAccessCode: Edge function error:', error);
        throw new Error(`Failed to create access code: ${error.message}`);
      }

      console.log('useCreateAccessCode: Edge function response:', data);

      // Determine which code to store - fallback code if used, otherwise original
      const codeToStore = data.fallback_used ? '2382' : accessCode;
      const isActiveFallback = data.fallback_used || true;
      
      // Extract the seam access code ID from the response
      const seamAccessCodeId = data.fallback_used 
        ? data.access_code?.access_code_id 
        : data.access_code?.access_code_id;

      // CRITICAL TIMEZONE FIX: Ensure we store the exact UTC timestamp
      // PostgreSQL can interpret ISO strings differently, so we need to be explicit
      const utcStartTime = new Date(startTime).toISOString();
      
      console.log('TIMEZONE DEBUG - DATABASE STORAGE:', {
        codeToStore,
        fallbackUsed: data.fallback_used,
        timeSlotId,
        seamAccessCodeId,
        DATABASE_INSERT_VALUES: {
          booking_id: bookingId,
          code: codeToStore,
          start_time_original: startTime,
          start_time_normalized: utcStartTime,
          time_slot_id: timeSlotId,
          is_active: isActiveFallback,
          seam_access_code_id: seamAccessCodeId,
          CRITICAL_CHECK: {
            input_start_time: startTime,
            normalized_start_time: utcStartTime,
            are_they_same: startTime === utcStartTime,
            start_time_local_display: new Date(utcStartTime).toLocaleString('en-GB', { timeZone: 'Europe/London' }),
            note: 'Using normalized UTC timestamp to prevent DB timezone interpretation issues'
          }
        }
      });

      // Store the access code in our database with normalized UTC timestamp, time_slot_id, and seam_access_code_id
      const { data: dbData, error: dbError } = await supabase
        .from('access_codes')
        .insert({
          booking_id: bookingId,
          code: codeToStore,
          start_time: utcStartTime, // Use normalized UTC timestamp
          time_slot_id: timeSlotId, // Include time_slot_id
          seam_access_code_id: seamAccessCodeId, // Store Seam access code ID
          is_active: isActiveFallback,
        })
        .select()
        .single();

      if (dbError) {
        console.error('useCreateAccessCode: Database error:', dbError);
        throw new Error(`Failed to store access code: ${dbError.message}`);
      }

      console.log('useCreateAccessCode: Access code stored in database:', dbData);

      // Update the booking with check-in timestamp
      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          checked_in_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (updateError) {
        console.error('useCreateAccessCode: Failed to update booking check-in time:', updateError);
        // Don't fail the entire operation if timestamp update fails
      } else {
        console.log('useCreateAccessCode: Updated booking with check-in timestamp');
      }

      return {
        seamResponse: data,
        accessCode: dbData
      };
    },
    onSuccess: (data, variables) => {
      console.log('useCreateAccessCode: Success callback, invalidating queries for booking:', variables.bookingId);
      queryClient.invalidateQueries({ queryKey: ['access-codes', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking', variables.bookingId] }); // Invalidate specific booking query
      queryClient.invalidateQueries({ queryKey: ['provider-booking-detail'] }); // Invalidate all provider booking detail queries
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['provider-bookings'] });
      // Toast removed for customer booking page - using scroll to access details instead
    },
    onError: (error) => {
      console.error('useCreateAccessCode: Mutation error:', error);
      toast({
        title: "Failed to Generate Access Code",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    },
  });
};
